import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftAddon,
  Button,
  Divider,
  Heading,
  useToast,
  Badge,
  Flex
} from '@chakra-ui/react';

// Measurement constants for standardization (must match FloorPlanCanvas.js)
const MEASUREMENT_CONSTANTS = {
  PIXELS_PER_INCH: 1, // 1 pixel = 1 inch for simplicity
  INCHES_PER_FOOT: 12,
  STANDARD_GRID_FEET: 5, // 5 foot grid
  STANDARD_ROOM_FEET: 30, // 30x30 ft standard room
  
  // Calculated values
  get PIXELS_PER_FOOT() { return this.PIXELS_PER_INCH * this.INCHES_PER_FOOT; }, // 12 pixels = 1 foot
  get GRID_PIXELS() { return this.STANDARD_GRID_FEET * this.PIXELS_PER_FOOT; }, // 60 pixels = 5 feet
  get STANDARD_ROOM_PIXELS() { return this.STANDARD_ROOM_FEET * this.PIXELS_PER_FOOT; }, // 360 pixels = 30 feet
};

// Utility functions for measurement conversion
const pixelsToFeet = (pixels) => {
  return (pixels / MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT).toFixed(1);
};

const feetToPixels = (feet) => {
  return feet * MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT;
};

const formatDimensions = (width, height) => {
  const widthFt = pixelsToFeet(width);
  const heightFt = pixelsToFeet(height);
  return {
    pixels: `${Math.round(width)} × ${Math.round(height)} px`,
    feet: `${widthFt} × ${heightFt} ft`
  };
};

const ObjectPropertiesSidebar = ({ selectedObject, onUpdateObject, isOpen, onClose, onDeleteObject }) => {
  const toast = useToast();

  if (!isOpen || !selectedObject) {
    return null;
  }

  const handlePropertyChange = (property, value) => {
    const updatedObject = { ...selectedObject };
    
    // Update the property
    updatedObject[property] = value;
    
    // For rooms, windows, and doors, also update the data object
    if (selectedObject.data) {
      updatedObject.data = { ...selectedObject.data };
      if (property === 'width' || property === 'height') {
        updatedObject.data[property] = value;
      }
    }
    
    onUpdateObject(updatedObject);
  };

  const handleDelete = () => {
    if (!selectedObject) return;

    // Show confirmation toast
    toast({
      title: "Delete Object",
      description: `Are you sure you want to delete this ${selectedObject.type}?`,
      status: "warning",
      duration: 3000,
      isClosable: true,
      position: "top-right"
    });

    // Call the delete function from parent
    onDeleteObject(selectedObject);
  };

  const renderRoomProperties = () => (
    <VStack spacing={4} align="stretch">
      <Heading size="sm" color="gray.700">Room Properties</Heading>
      <Divider />
      
      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Width</Text>
        <VStack spacing={2} align="stretch">
          <InputGroup size="sm">
            <InputLeftAddon bg="blue.50">px</InputLeftAddon>
            <Input
              type="number"
              value={selectedObject.width || MEASUREMENT_CONSTANTS.STANDARD_ROOM_PIXELS}
              onChange={(e) => handlePropertyChange('width', parseFloat(e.target.value) || MEASUREMENT_CONSTANTS.STANDARD_ROOM_PIXELS)}
              min="60"
              max="1200"
            />
          </InputGroup>
          <InputGroup size="sm">
            <InputLeftAddon bg="green.50">ft</InputLeftAddon>
            <Input
              type="number"
              step="0.1"
              value={pixelsToFeet(selectedObject.width || MEASUREMENT_CONSTANTS.STANDARD_ROOM_PIXELS)}
              onChange={(e) => handlePropertyChange('width', feetToPixels(parseFloat(e.target.value) || MEASUREMENT_CONSTANTS.STANDARD_ROOM_FEET))}
              min="5"
              max="100"
            />
          </InputGroup>
        </VStack>
      </Box>

      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Height</Text>
        <VStack spacing={2} align="stretch">
          <InputGroup size="sm">
            <InputLeftAddon bg="blue.50">px</InputLeftAddon>
            <Input
              type="number"
              value={selectedObject.height || MEASUREMENT_CONSTANTS.STANDARD_ROOM_PIXELS}
              onChange={(e) => handlePropertyChange('height', parseFloat(e.target.value) || MEASUREMENT_CONSTANTS.STANDARD_ROOM_PIXELS)}
              min="60"
              max="1200"
            />
          </InputGroup>
          <InputGroup size="sm">
            <InputLeftAddon bg="green.50">ft</InputLeftAddon>
            <Input
              type="number"
              step="0.1"
              value={pixelsToFeet(selectedObject.height || MEASUREMENT_CONSTANTS.STANDARD_ROOM_PIXELS)}
              onChange={(e) => handlePropertyChange('height', feetToPixels(parseFloat(e.target.value) || MEASUREMENT_CONSTANTS.STANDARD_ROOM_FEET))}
              min="5"
              max="100"
            />
          </InputGroup>
        </VStack>
      </Box>

      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Stroke Width (px)</Text>
        <InputGroup size="sm">
          <InputLeftAddon>S</InputLeftAddon>
          <Input
            type="number"
            value={selectedObject.strokeWidth || 4}
            onChange={(e) => handlePropertyChange('strokeWidth', parseFloat(e.target.value) || 4)}
            min="1"
            max="10"
          />
        </InputGroup>
      </Box>

      <Button 
        colorScheme="red" 
        size="sm" 
        onClick={handleDelete}
        leftIcon={<span>🗑️</span>}
      >
        Delete Room
      </Button>
    </VStack>
  );

  const renderWindowProperties = () => (
    <VStack spacing={4} align="stretch">
      <Heading size="sm" color="gray.700">Window Properties</Heading>
      <Divider />
      
      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Length</Text>
        <VStack spacing={2} align="stretch">
          <InputGroup size="sm">
            <InputLeftAddon bg="blue.50">px</InputLeftAddon>
            <Input
              type="number"
              value={selectedObject.height || MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 10}
              onChange={(e) => handlePropertyChange('height', parseFloat(e.target.value) || MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 10)}
              min="12"
              max="240"
            />
          </InputGroup>
          <InputGroup size="sm">
            <InputLeftAddon bg="green.50">ft</InputLeftAddon>
            <Input
              type="number"
              step="0.1"
              value={pixelsToFeet(selectedObject.height || MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 10)}
              onChange={(e) => handlePropertyChange('height', feetToPixels(parseFloat(e.target.value) || 10))}
              min="1"
              max="20"
            />
          </InputGroup>
        </VStack>
        <Text fontSize="xs" color="gray.500" mt={1}>
          Width is automatically adjusted based on wall type
        </Text>
      </Box>

      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Current Dimensions</Text>
        <VStack spacing={1} align="start">
          <Text fontSize="xs" color="gray.600">
            {Math.round(selectedObject.width || 8)} × {Math.round(selectedObject.height || MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 10)} px
          </Text>
          <Text fontSize="xs" color="green.600" fontWeight="medium">
            {pixelsToFeet(selectedObject.width || 8)} × {pixelsToFeet(selectedObject.height || MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 10)} ft
          </Text>
        </VStack>
      </Box>

      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Stroke Width (px)</Text>
        <InputGroup size="sm">
          <InputLeftAddon>S</InputLeftAddon>
          <Input
            type="number"
            value={selectedObject.strokeWidth || 1}
            onChange={(e) => handlePropertyChange('strokeWidth', parseFloat(e.target.value) || 1)}
            min="1"
            max="5"
          />
        </InputGroup>
      </Box>

      <Button 
        colorScheme="red" 
        size="sm" 
        onClick={handleDelete}
        leftIcon={<span>🗑️</span>}
      >
        Delete Window
      </Button>
    </VStack>
  );

  const renderDoorProperties = () => (
    <VStack spacing={4} align="stretch">
      <Heading size="sm" color="gray.700">Door Properties</Heading>
      <Divider />
      
      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Width</Text>
        <VStack spacing={2} align="stretch">
          <InputGroup size="sm">
            <InputLeftAddon bg="blue.50">px</InputLeftAddon>
            <Input
              type="number"
              value={selectedObject.height || MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 3}
              onChange={(e) => handlePropertyChange('height', parseFloat(e.target.value) || MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 3)}
              min="24"
              max="120"
            />
          </InputGroup>
          <InputGroup size="sm">
            <InputLeftAddon bg="green.50">ft</InputLeftAddon>
            <Input
              type="number"
              step="0.1"
              value={pixelsToFeet(selectedObject.height || MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 3)}
              onChange={(e) => handlePropertyChange('height', feetToPixels(parseFloat(e.target.value) || 3))}
              min="2"
              max="10"
            />
          </InputGroup>
        </VStack>
        <Text fontSize="xs" color="gray.500" mt={1}>
          Height is automatically adjusted based on wall type
        </Text>
      </Box>

      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Current Dimensions</Text>
        <VStack spacing={1} align="start">
          <Text fontSize="xs" color="gray.600">
            {Math.round(selectedObject.width || 8)} × {Math.round(selectedObject.height || MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 3)} px
          </Text>
          <Text fontSize="xs" color="green.600" fontWeight="medium">
            {pixelsToFeet(selectedObject.width || 8)} × {pixelsToFeet(selectedObject.height || MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 3)} ft
          </Text>
        </VStack>
      </Box>

      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Stroke Width (px)</Text>
        <InputGroup size="sm">
          <InputLeftAddon>S</InputLeftAddon>
          <Input
            type="number"
            value={selectedObject.strokeWidth || 1}
            onChange={(e) => handlePropertyChange('strokeWidth', parseFloat(e.target.value) || 1)}
            min="1"
            max="5"
          />
        </InputGroup>
      </Box>

      <Button 
        colorScheme="red" 
        size="sm" 
        onClick={handleDelete}
        leftIcon={<span>🗑️</span>}
      >
        Delete Door
      </Button>
    </VStack>
  );

  const renderWallProperties = () => (
    <VStack spacing={4} align="stretch">
      <Heading size="sm" color="gray.700">Wall Properties</Heading>
      <Divider />
      
      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Stroke Width (px)</Text>
        <InputGroup size="sm">
          <InputLeftAddon>S</InputLeftAddon>
          <Input
            type="number"
            value={selectedObject.strokeWidth || 4}
            onChange={(e) => handlePropertyChange('strokeWidth', parseFloat(e.target.value) || 4)}
            min="1"
            max="10"
          />
        </InputGroup>
      </Box>
    </VStack>
  );

  const renderProductProperties = () => (
    <VStack spacing={4} align="stretch">
      <Heading size="sm" color="gray.700">Product Properties</Heading>
      <Divider />
      
      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Product Name</Text>
        <Text fontSize="sm" color="gray.600">
          {selectedObject.data?.productName || 'Unknown Product'}
        </Text>
      </Box>

      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Size (fixed)</Text>
        <VStack spacing={1} align="stretch">
          <InputGroup size="sm">
            <InputLeftAddon bg="blue.50">px</InputLeftAddon>
            <Input
              value={`${MEASUREMENT_CONSTANTS.GRID_PIXELS}`}
              isReadOnly
              bg="gray.100"
            />
          </InputGroup>
          <InputGroup size="sm">
            <InputLeftAddon bg="green.50">ft</InputLeftAddon>
            <Input
              value={`${MEASUREMENT_CONSTANTS.STANDARD_GRID_FEET}`}
              isReadOnly
              bg="green.50"
            />
          </InputGroup>
        </VStack>
        <Text fontSize="xs" color="gray.500" mt={1}>
          Product size is fixed at {MEASUREMENT_CONSTANTS.GRID_PIXELS}×{MEASUREMENT_CONSTANTS.GRID_PIXELS}px ({MEASUREMENT_CONSTANTS.STANDARD_GRID_FEET}×{MEASUREMENT_CONSTANTS.STANDARD_GRID_FEET}ft)
        </Text>
      </Box>

      {selectedObject.data?.productData && (
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2}>Product Details</Text>
          <Text fontSize="xs" color="gray.500">
            {JSON.stringify(selectedObject.data.productData, null, 2)}
          </Text>
        </Box>
      )}

      <Button 
        colorScheme="red" 
        size="sm" 
        onClick={handleDelete}
        leftIcon={<span>🗑️</span>}
      >
        Delete Product
      </Button>
    </VStack>
  );

  const renderObjectInfo = () => (
    <VStack spacing={4} align="stretch">
      <Heading size="sm" color="gray.700">Object Information</Heading>
      <Divider />
      
      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Type</Text>
        <Text fontSize="sm" color="gray.600" textTransform="capitalize">
          {selectedObject.type}
        </Text>
      </Box>

      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Position</Text>
        <VStack spacing={1} align="start">
          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600">
              X: {Math.round(selectedObject.x || 0)}, Y: {Math.round(selectedObject.y || 0)} px
            </Text>
            <Badge colorScheme="blue" variant="subtle" fontSize="xs">
              Pixels
            </Badge>
          </Flex>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="green.600" fontWeight="medium">
              X: {pixelsToFeet(selectedObject.x || 0)}, Y: {pixelsToFeet(selectedObject.y || 0)} ft
            </Text>
            <Badge colorScheme="green" variant="subtle" fontSize="xs">
              Feet
            </Badge>
          </Flex>
        </VStack>
      </Box>

      {selectedObject.width && selectedObject.height && (
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2}>Dimensions</Text>
          <VStack spacing={1} align="start">
            <Flex align="center" gap={2}>
              <Text fontSize="sm" color="gray.600">
                {Math.round(selectedObject.width)} × {Math.round(selectedObject.height)} px
              </Text>
              <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                Pixels
              </Badge>
            </Flex>
            <Flex align="center" gap={2}>
              <Text fontSize="sm" color="green.600" fontWeight="medium">
                {pixelsToFeet(selectedObject.width)} × {pixelsToFeet(selectedObject.height)} ft
              </Text>
              <Badge colorScheme="green" variant="subtle" fontSize="xs">
                Feet
              </Badge>
            </Flex>
          </VStack>
        </Box>
      )}
    </VStack>
  );

  return (
    <Box
      position="absolute"
      right={0}
      top={0}
      width="300px"
      height="100%"
      bg="white"
      borderLeft="1px solid"
      borderColor="gray.200"
      p={4}
      overflowY="auto"
      zIndex={1000}
    >
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" align="center">
          <Heading size="md" color="gray.800">Properties</Heading>
          <Button size="sm" variant="ghost" onClick={onClose}>
            ✕
          </Button>
        </HStack>

        {renderObjectInfo()}

        {selectedObject.type === 'room' && renderRoomProperties()}
        {selectedObject.type === 'window' && renderWindowProperties()}
        {selectedObject.type === 'door' && renderDoorProperties()}
        {selectedObject.type === 'wall' && renderWallProperties()}
        {selectedObject.type === 'product' && renderProductProperties()}
      </VStack>
    </Box>
  );
};

export default ObjectPropertiesSidebar; 